import { Location } from 'history';
import moment from 'moment';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  TimeRangeOption,
  defaultTimeRange,
} from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { useCurrentPlan } from './currentPlanV2Migration';
import { useInsightsFilter } from './useInsightsFilter';

export const resetTimeRangeLocation = (location: Location) =>
  insightsRouteConfig.locationFrom({
    location,
    patch: {
      range: RouteConfig.DELETE,
      from: RouteConfig.DELETE,
      to: RouteConfig.DELETE,
      fastModeRange: RouteConfig.DELETE,
    },
  });

export enum TimeRangeErrorCode {
  NOT_AVAILABLE = 'not-available',
  INVALID = 'invalid',
  PLAN_ERROR = 'plan-error',
}

export class TimeRangeError extends Error {
  code: TimeRangeErrorCode;
  constructor(code: TimeRangeErrorCode) {
    super();
    this.code = code;
  }
}

export interface TimeRangeInfo {
  accountCapabilities: {
    maxRangeInDays: number;
    ranges: ReadonlyArray<string>;
  };
  selectedRange: TimeRangeOption;
  timeFrom: Date | string;
  timeTo: Date | string;
}

export type UseSelectedTimeRangeResult =
  | {
      loading: true;
      error?: TimeRangeError;
      timeRangeInfo?: TimeRangeInfo;
    }
  | {
      loading: false;
      error: TimeRangeError;
      timeRangeInfo?: TimeRangeInfo;
    }
  | {
      loading: false;
      error?: TimeRangeError;
      timeRangeInfo: TimeRangeInfo;
    };

export function validateTimeRange({
  allowedRanges,
  defaultRange = defaultTimeRange,
  maxRangeInDays,
  routeParams,
}: {
  allowedRanges: Array<TimeRangeOption>;
  defaultRange?: TimeRangeOption;
  maxRangeInDays: number;
  routeParams: {
    range?: TimeRangeOption;
    from?: Date | string;
    to?: Date | string;
  };
}):
  | { validRange: TimeRangeOption; error?: undefined }
  | { validRange?: undefined; error: TimeRangeError } {
  const requestedRange =
    routeParams.range ||
    (allowedRanges.includes(defaultRange) ? defaultRange : allowedRanges[0]);

  if (!requestedRange || !allowedRanges.includes(requestedRange)) {
    console.warn(`time range "${requestedRange}" unavailable`); // eslint-disable-line no-console
    return {
      validRange: undefined,
      error: new TimeRangeError(TimeRangeErrorCode.NOT_AVAILABLE),
    };
  }
  if (requestedRange === 'custom') {
    try {
      // this will throw if validation fails
      date.validateDateSelection(
        moment(routeParams.from).toDate(),
        moment(routeParams.to).toDate(),
        moment.duration(maxRangeInDays, 'days'),
      );
    } catch (e) {
      const error = e as Error;
      console.warn(error.message); // eslint-disable-line no-console
      return {
        validRange: undefined,
        error: new TimeRangeError(TimeRangeErrorCode.INVALID),
      };
    }
  }
  return { validRange: requestedRange, error: undefined };
}

/**
 * A hook to read the time filter state from the URL.
 * Uses the current plan capabilities to determine if the time range is
 * allowed, and if not, may return an error in `result.error`.
 */
export function useSelectedTimeRange(): UseSelectedTimeRangeResult {
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { routeParams } = useInsightsFilter();

  // Since the current account ID is set elsewhere in the application,
  // and this happens asynchronously, treat this as a loading state as well.
  if (currentPlan.loading || !currentAccountId) {
    return {
      loading: true,
    };
  }
  if (!currentPlan.currentPlan || currentPlan.error) {
    return {
      loading: false,
      error: new TimeRangeError(TimeRangeErrorCode.PLAN_ERROR),
    };
  }
  const { currentPlan: capabilities } = currentPlan;
  const maxRangeInDays =
    capabilities.maxRangeInDays !== null ? capabilities.maxRangeInDays : 0;
  const ranges = (capabilities.ranges || []) as TimeRangeOption[];

  const validationResult = validateTimeRange({
    allowedRanges: ranges,
    maxRangeInDays,
    routeParams,
  });
  if (validationResult.error) {
    return {
      loading: false,
      error: validationResult.error,
    };
  }

  const { validRange } = validationResult;
  const { timeFrom, timeTo } = date.getDateBounds(
    validRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
    undefined,
    routeParams.fastModeRange,
  );
  return {
    loading: false,
    timeRangeInfo: {
      selectedRange: validRange,
      accountCapabilities: {
        maxRangeInDays,
        ranges,
      },
      timeFrom,
      timeTo,
    },
  };
}
